<template>
  <card id="settings" :title="$t('settings:title')">
    <small slot="card-header-right" class="text-muted font-italic">{{ $t('settings:required') }}</small>

    <template slot="card-body">
      <h3 class="mb-4">{{ $t('settings:gateways') | capitalize }}</h3>

      <div class="form-group">
        <input id="remoteShellActivation" v-model="remoteShellActivation" type="checkbox">
        <label for="remoteShellActivation" />
        <label for="remoteShellActivation">{{ $t('settings:remoteShellActivation') | capitalize }}</label>
      </div>

      <div class="form-row">
        <div class="form-group col-md-3">
          <label for="healthRefresh">{{ $t('settings:healthRefresh') | capitalize }} *</label>
          <input id="healthRefresh"
                 v-model="healthRefresh"
                 v-validate="'required|number|between:5,3600'"
                 :data-vv-as="$t('settings:healthRefresh')"
                 :class="{ 'is-invalid': errors.has('healthRefresh') }"
                 type="text"
                 class="form-control"
                 name="healthRefresh"
          >
          <small class="form-text text-muted">{{ $t('settings:betweenSeconds', { min: 5, max: 3600 }) | capitalize }}</small>
          <div class="invalid-feedback">{{ errors.first('healthRefresh') }}</div>
        </div>
      </div>

      <h3 class="pt-2 my-4">{{ $t('settings:alarms') | capitalize }}</h3>

      <div class="form-row">
        <div class="form-group col-md-3">
          <label for="alarmsRefresh">{{ $t('settings:alarmsRefresh') | capitalize }} *</label>
          <input id="alarmsRefresh"
                 v-model="alarmsRefresh"
                 v-validate="'required|number|between:5,3600'"
                 :data-vv-as="$t('settings:alarmsRefresh')"
                 :class="{ 'is-invalid': errors.has('alarmsRefresh') }"
                 type="text"
                 class="form-control"
                 name="alarmsRefresh"
          >
          <small class="form-text text-muted">{{ $t('settings:betweenSeconds', { min: 5, max: 3600 }) | capitalize }}</small>
          <div class="invalid-feedback">{{ errors.first('alarmsRefresh') }}</div>
        </div>
      </div>

      <h3 class="pt-2 my-4">{{ $t('settings:leafletTileServer') | capitalize }}</h3>

      <div class="form-row">
        <div class="form-group col-md-8">
          <label for="tilesServerUrl">{{ $t('settings:tilesServerUrl') | capitalize }} *</label>
          <input id="tilesServerUrl"
                 v-model="tilesServerUrl"
                 v-validate="'required|tilesProviderUrl'"
                 :data-vv-as="$t('settings:tilesServerUrl')"
                 :class="{ 'is-invalid': errors.has('tilesServerUrl') }"
                 class="form-control"
                 type="text"
                 name="tilesServerUrl"
          >
          <small class="form-text text-muted">
            {{ $t('settings:tilesServerUrlHelper') | capitalize }} <a href="https://leafletjs.com/reference.html#tilelayer" target="_blank">{{ $t('settings:tilesLayerFormat') | capitalize }}</a>
          </small>
          <small v-if="/(osm|openstreetmap)\.org/.test(tilesServerUrl)" class="form-text text-muted">
            <a href="https://operations.osmfoundation.org/policies/tiles/" target="_blank">{{ $t('settings:tilesServerUsagePolicy') | capitalize }}</a>
          </small>
          <div class="invalid-feedback">{{ errors.first('tilesServerUrl') }}</div>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group col-md-8">
          <label for="tilesServerAttribution">{{ $t('settings:tilesServerAttribution') | capitalize }} *</label>
          <input id="tilesServerAttribution"
                 v-model="tilesServerAttribution"
                 v-validate="'required'"
                 :data-vv-as="$t('settings:tilesServerAttribution')"
                 :class="{ 'is-invalid': errors.has('tilesServerAttribution') }"
                 class="form-control"
                 name="tilesServerAttribution"
          >
          <div class="invalid-feedback">{{ errors.first('tilesServerAttribution') }}</div>
        </div>
      </div>
    </template>

    <template slot="card-footer">
      <button id="updateButton" class="btn btn-primary" @click="saveSettings">{{ $t('settings:update') }}</button>
    </template>
  </card>
</template>

<script>
import { map } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

export default {
  titleKey: 'settings',

  data () {
    return {
      alarmsRefresh: 0,
      healthRefresh: 0,
      remoteShellActivation: 0,
      tilesServerUrl: '',
      tilesServerAttribution: ''
    }
  },

  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters(['dashboardSettings'])
  },

  mounted () {
    // Convert in seconds
    this.alarmsRefresh = this.dashboardSettings.alarmsRefresh / 1000
    this.healthRefresh = this.dashboardSettings.healthRefresh / 1000

    this.remoteShellActivation = this.dashboardSettings.remoteShellActivation

    this.tilesServerUrl = this.dashboardSettings.tilesServerUrl
    this.tilesServerAttribution = this.dashboardSettings.tilesServerAttribution
  },

  methods: {
    // Create setting object
    addSetting (key, value) {
      return {
        customer: { id: this.$store.getters.authenticatedUser.customer.id },
        category: 'dashboard',
        key: key,
        value: value
      }
    },

    // Create save setting request
    async saveSettings () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      const settings = []
      settings.push(this.addSetting('alarmsRefresh', this.alarmsRefresh * 1000))
      settings.push(this.addSetting('healthRefresh', this.healthRefresh * 1000))
      settings.push(this.addSetting('remoteShellActivation', this.remoteShellActivation))
      settings.push(this.addSetting('tilesServerUrl', this.tilesServerUrl))
      settings.push(this.addSetting('tilesServerAttribution', this.tilesServerAttribution))

      let error
      [error] = await to(this.$api.createCustomersSettings(
        this.$store.getters.application,
        map(settings, setting => {
          setting.value = JSON.stringify(setting.value)
          return setting
        })
      ))
      if (error) return

      // Get new settings to apply it
      this.$store.dispatch('getSettings')
      // Success
      eventBus.$emit('message-success', 'Application settings updated')
    }
  }
}
</script>
